// Based on https://github.com/ariakit/ariakit/blob/2172b4f709d5904594478a39e71f540690fc8955/packages/ariakit/src/dialog/__utils/use-prevent-body-scroll.ts
// When moving to ariakit, this file can be removed.
import { useEffect, useLayoutEffect } from 'react';

/**
 * `React.useLayoutEffect` that fallbacks to `React.useEffect` on server side.
 */
export const useSafeLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function assignStyle(element: HTMLElement | null | undefined, style: Partial<CSSStyleDeclaration>) {
    if (!element) return () => null;
    const previousStyle = element.style.cssText;
    Object.assign(element.style, style);
    return () => {
        element.style.cssText = previousStyle;
    };
}

export function usePreventBodyScroll(enabled?: boolean) {
    useSafeLayoutEffect(() => {
        if (!enabled) return;
        const { documentElement, body } = document;
        const scrollbarWidth = window.innerWidth - documentElement.clientWidth;
        const scrollX = window.pageXOffset;
        const scrollY = window.pageYOffset;

        // RTL <body> scrollbar
        const documentLeft = documentElement.getBoundingClientRect().left;
        const scrollbarX = Math.round(documentLeft) + documentElement.scrollLeft;
        const paddingProp = scrollbarX ? 'paddingLeft' : 'paddingRight';

        const restoreStyle = assignStyle(body, {
            position: 'fixed',
            overflow: 'hidden',
            top: `-${scrollY}px`,
            left: `-${scrollX}px`,
            right: '0',
            [paddingProp]: `${scrollbarWidth}px`,
        });

        const previousScrollbarWidth = documentElement.style.getPropertyValue('--scrollbar-width');

        documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);

        return () => {
            restoreStyle();
            // istanbul ignore next: JSDOM doesn't implement window.scrollTo
            if (process.env['NODE_ENV'] !== 'test') {
                window.scrollTo(scrollX, scrollY);
            }
            if (previousScrollbarWidth) {
                documentElement.style.setProperty('--scrollbar-width', previousScrollbarWidth);
            } else {
                documentElement.style.removeProperty('--scrollbar-width');
            }
        };
    }, [enabled]);
}
